import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ShieldCheckIcon, MinusIcon, PlusIcon } from 'lucide-react';
const PORTRAIT_IMG = 'https://cdn.magicpatterns.com/uploads/tCQJHjZa55p75RS9fGnYhW/Banner_cuadrado_para_Beatriz.jpg';
const MOCKUP_IMG = 'https://cdn.magicpatterns.com/uploads/a5GNQvpiyTfKdHL3uEfeVq/Imagen_2.jpg';
const COVER_IMG = 'https://cdn.magicpatterns.com/uploads/f6Cj579oe2vxk1CSGHa3Rj/Imagen_3.jpg';
const CHECKOUT_URL = 'https://pay.hotmart.com/K98488159N?off=dnutfmp1&checkoutMode=10';
const DEADLINE = new Date('2026-10-01T22:00:00-03:00').getTime();
const FAQ_ITEMS: {
  q: string;
  a: React.ReactNode;
}[] = [{
  q: '¿Cómo sé si esto es para mí?',
  a: 'Si sientes que das todo y aun así no te ven, es para ti.'
}, {
  q: '¿Es terapia o es un curso?',
  a: 'Es un método conductual con acompañamiento real, no solo contenido.'
}, {
  q: '¿Cuánto dura?',
  a: '30 días de programa, con acceso a las clases por 1 año.'
}, {
  q: '¿A qué hora son las sesiones en vivo?',
  a: <>
        <span className="block" data-id="element-0" data-mp="3ii4sg33z0up3">
          1 y 29 de octubre a las 10:00 pm hora Argentina.
        </span>
        <span className="mt-2 block" data-id="element-1" data-mp="3rc9em985thya">
          Referencia: 10:00 pm Chile · 9:00 pm Venezuela y Miami · 8:00 pm
          Colombia, Perú y Ecuador · 7:00 pm México.
        </span>
      </>
}, {
  q: '¿Qué pasa si no puedo asistir en vivo?',
  a: 'Las sesiones quedan grabadas para que las veas cuando puedas.'
}, {
  q: '¿Puedo pagar desde mi país?',
  a: 'Sí. Hotmart acepta tarjetas internacionales y métodos de pago locales según tu país.'
}, {
  q: '¿Tiene garantía?',
  a: 'Sí. Tienes 7 días de garantía. Si no es para ti, solicitas tu reembolso a través de Hotmart.'
}];
const INCLUDES = ['Sesión en vivo de diagnóstico: 1 de octubre', 'Sesión en vivo de cierre: 29 de octubre', 'Ambas sesiones quedan grabadas', '30 días de acompañamiento por WhatsApp', '+30 clases en Hotmart (acceso por 1 año)', 'Comunidad privada Mujer Elegida'];
const FOR_YOU = ['Das todo en tus relaciones y aun así no te eligen.', 'Eres la fuerte, la que resuelve, la que sostiene.', 'Atraes hombres que no se comprometen.', 'Quieres entender tu patrón, no solo sentirte mejor un día.'];
const NOT_FOR_YOU = ['Buscas una fórmula para "conquistar" a alguien en particular.', 'No estás dispuesta a revisar tu propia conducta.'];
type Remaining = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  finished: boolean;
};
function getRemaining(): Remaining {
  const diff = DEADLINE - Date.now();
  if (diff <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      finished: true
    };
  }
  const totalSeconds = Math.floor(diff / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor(totalSeconds % 86400 / 3600),
    minutes: Math.floor(totalSeconds % 3600 / 60),
    seconds: totalSeconds % 60,
    finished: false
  };
}
const pad = (n: number) => String(n).padStart(2, '0');
export function App() {
  const heroRef = useRef<HTMLElement | null>(null);
  const offerRef = useRef<HTMLElement | null>(null);
  const [remaining, setRemaining] = useState<Remaining>(getRemaining);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [pastHero, setPastHero] = useState(false);
  const [offerVisible, setOfferVisible] = useState(false);
  useEffect(() => {
    const interval = setInterval(() => setRemaining(getRemaining()), 1000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    const hero = heroRef.current;
    const offer = offerRef.current;
    if (!hero || !offer) return;
    const heroObserver = new IntersectionObserver(([entry]) => setPastHero(!entry.isIntersecting), {
      threshold: 0
    });
    const offerObserver = new IntersectionObserver(([entry]) => setOfferVisible(entry.isIntersecting), {
      threshold: 0.15
    });
    heroObserver.observe(hero);
    offerObserver.observe(offer);
    return () => {
      heroObserver.disconnect();
      offerObserver.disconnect();
    };
  }, []);
  const scrollToOffer = useCallback(() => {
    offerRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, []);
  const showStickyBar = pastHero && !offerVisible;
  const countdownBoxes = useMemo(() => [{
    label: 'DÍAS',
    value: String(remaining.days)
  }, {
    label: 'HORAS',
    value: pad(remaining.hours)
  }, {
    label: 'MIN',
    value: pad(remaining.minutes)
  }, {
    label: 'SEG',
    value: pad(remaining.seconds)
  }], [remaining]);
  return <div className="w-full min-h-full bg-ink font-sans text-white" data-id="element-2" data-mp="zwsg6fro3m1f">
      <main className="mx-auto w-full max-w-[480px] pb-24 md:pb-0" data-id="element-3" data-mp="vwibd6fm31o5">
        {/* SECCIÓN 1 — HERO */}
        <section ref={heroRef} className="bg-brand px-6 py-16 text-center" aria-labelledby="hero-title" data-id="element-4" data-mp="3kokk9alp8wxs">
          <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-5" data-mp="3gkwf0ykjidfp">
            MSc Beatriz Espina · La Doc
          </p>
          <h1 id="hero-title" className="mt-6 font-display text-[34px] font-bold leading-[1.15]" data-id="element-6" data-mp="205quuyjx26xw">
            Eres un <span className="text-gold" data-id="element-7" data-mp="254blq7da3e9o">10/10</span>. ¿Por qué nadie te
            ve?
          </h1>
          <p className="mx-auto mt-5 text-[17px] leading-[1.6] text-muted" data-id="element-8" data-mp="29wlia68wfckx">
            El patrón de conducta que te hizo exitosa es el mismo que te está
            haciendo invisible.
          </p>
          <img src={PORTRAIT_IMG} alt="ELEGIDA — MSc Beatriz Espina, Estratega mental" className="mx-auto mt-8 w-[85%] rounded-2xl object-contain" data-id="element-9" data-mp="2jsomdhnb9lai" />
          <div className="mt-8 border-y border-gold/35 py-7" data-id="element-10" data-mp="2stedi81mmnhw">
            <p className="text-[18px] leading-[1.6] text-white" data-id="element-11" data-mp="thtrtyvbcfe9">
              En <span className="font-semibold text-gold" data-id="element-12" data-mp="tjo82zvn3jc0">30 días</span>{' '}
              dejarás de ser invisible para convertirte en la mujer que un
              hombre{' '}
              <span className="font-semibold text-gold" data-id="element-13" data-mp="21s3xd3ubuqmm">
                elige, desea y no quiere soltar
              </span>
              .
            </p>
          </div>

          <button type="button" onClick={scrollToOffer} className="mt-8 h-14 w-full rounded-xl bg-gold text-[15px] font-bold uppercase tracking-wide text-ink shadow-gold transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-goldbright hover:shadow-gold-strong active:translate-y-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-14" data-mp="2asiju6j12brp">
            Quiero entender por qué
          </button>
          <p className="mt-4 text-[13px] leading-[1.6] text-muted" data-id="element-15" data-mp="2pig6uj580vfb">
            Programa de 30 días · Inicia el 1 de octubre
          </p>
        </section>

        {/* SECCIÓN 2 — CONTADOR */}
        <section className="border-y border-gold bg-ink px-6 py-16 text-center" aria-label="Cierre de inscripciones" data-id="element-16" data-mp="2yj5v91s5jjj0">
          <p className="text-[17px] leading-[1.6] text-muted" data-id="element-17" data-mp="3n2pqjeralizz">
            Las inscripciones cierran el 1 de octubre, al iniciar el diagnóstico
            en vivo.
          </p>

          {remaining.finished ? <p className="mt-8 font-display text-[28px] font-bold text-gold" data-id="element-18" data-mp="3d4a2jknkapup">
              Inscripciones cerradas
            </p> : <div className="mt-8 grid grid-cols-4 gap-2" role="timer" data-id="element-19" data-mp="2zbkezbsdxcvh">
              {countdownBoxes.map(box => <div key={box.label} className="rounded-xl border border-gold/35 bg-white/[0.03] px-1 py-4" data-id="element-20" data-mp="345ycbjqlxu2s">
                  <div className="font-display text-[30px] font-bold leading-none text-gold tabular-nums" data-id="element-21" data-mp="2uetxql4ugb8h">
                    {box.value}
                  </div>
                  <div className="mt-2 text-[11px] font-medium uppercase tracking-[0.12em] text-muted" data-id="element-22" data-mp="2kfmjlvqacckb">
                    {box.label}
                  </div>
                </div>)}
            </div>}

          <button type="button" onClick={scrollToOffer} className="mt-9 h-14 w-full rounded-xl bg-gold text-[15px] font-bold uppercase tracking-wide text-ink shadow-gold transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-goldbright hover:shadow-gold-strong active:translate-y-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-23" data-mp="2vxc0zom0ezt4">
            Reservar mi cupo
          </button>
        </section>

        {/* SECCIÓN 3 — ESPEJO */}
        <section className="bg-brand-up px-6 py-16" aria-labelledby="espejo" data-id="element-24" data-mp="237n1xgsv19u5">
          <h2 id="espejo" className="font-display text-[28px] font-bold leading-[1.2]" data-id="element-25" data-mp="235inw618yug5">
            Llegas sola. Otra vez.
          </h2>
          <div className="mt-6 space-y-3 text-[17px] leading-[1.6] text-muted" data-id="element-26" data-mp="1u7gpzzct8hx3">
            <p data-id="element-27" data-mp="9nusjd08u1q1">Preparada. Exitosa. Un 10/10 en todo, menos en esto.</p>
            <p data-id="element-28" data-mp="dq7ya1ezpzmt">No es que te falte algo.</p>
            <p data-id="element-29" data-mp="dp6nzz5sth65">Es que nadie te enseñó a ver tu propio patrón.</p>
          </div>
        </section>

        {/* SECCIÓN 4 — EL MECANISMO */}
        <section className="bg-ink px-6 py-16" aria-labelledby="mecanismo" data-id="element-30" data-mp="inridhydd6mo">
          <div className="md:flex md:items-start md:gap-6" data-id="element-31" data-mp="nnw2dhyhkjhn">
            <div className="md:flex-1" data-id="element-32" data-mp="3m72rsq9we7o8">
              <h2 id="mecanismo" className="font-display text-[28px] font-bold leading-[1.2]" data-id="element-33" data-mp="3m4850s3ewarn">
                No es tu belleza. Es tu conducta.
              </h2>
              <div className="mt-6 space-y-3 text-[17px] leading-[1.6] text-muted" data-id="element-34" data-mp="3w2nxaye6dq2q">
                <p data-id="element-35" data-mp="4r4fyznndo9d">
                  Años resolviendo, sosteniendo, demostrando… te volvieron
                  eficiente.
                </p>
                <p data-id="element-36" data-mp="38ed0q7fiick9">
                  Y sin darte cuenta, esa misma fuerza apaga tu magnetismo.
                </p>
              </div>
              <blockquote className="mt-7 border-l-2 border-gold pl-4" data-id="element-37" data-mp="2f6lpdcu5l4wy">
                <p className="text-[17px] font-bold leading-[1.6] text-white" data-id="element-38" data-mp="2k5f2v59fwvx4">
                  Se llama conducta hipermasculinizada. Y se puede reeducar.
                </p>
              </blockquote>
            </div>
            <img src={MOCKUP_IMG} alt="MSc Beatriz Espina" className="mt-8 w-full rounded-2xl object-contain md:mt-0 md:w-[45%]" data-id="element-39" data-mp="2u331i6a62nkk" />
          </div>
        </section>

        {/* SECCIÓN 5 — HISTORIA */}
        <section className="bg-brand px-6 py-16" aria-labelledby="historia" data-id="element-40" data-mp="26f3ar6cuux5z">
          <h2 id="historia" className="font-display text-[28px] font-bold leading-[1.2]" data-id="element-41" data-mp="26cqmkbgi7qkh">
            A mí también me dejaron por un mensaje de texto.
          </h2>
          <div className="mt-6 space-y-3 text-[17px] leading-[1.6] text-muted" data-id="element-42" data-mp="1dobi2iv00qne">
            <p data-id="element-43" data-mp="1inedrcp39kip">10 años juntos. Me adapté para ser "la elegida".</p>
            <p data-id="element-44" data-mp="3vyh5dqf2j13i">
              Y aun así, me dejó por WhatsApp, mientras yo daba una charla sobre
              sexualidad.
            </p>
            <p data-id="element-45" data-mp="3rvsvrgij5q5h">Ese día entendí el patrón que hoy enseño.</p>
          </div>
          <p className="mt-7 font-display text-[19px] italic text-gold" data-id="element-46" data-mp="3mxixgx1ie9fn">
            — MSc Beatriz Espina
          </p>
        </section>

        {/* SECCIÓN 6 — PARA QUIÉN ES */}
        <section className="bg-ink px-6 py-16" aria-labelledby="para-quien" data-id="element-47" data-mp="3d0wiupzs0em2">
          <h2 id="para-quien" className="font-display text-[28px] font-bold leading-[1.2]" data-id="element-48" data-mp="38a69z2s55tmm">
            ELEGIDA es para ti si…
          </h2>
          <ul className="mt-6 space-y-4" data-id="element-49" data-mp="345yiixuatzkp">
            {FOR_YOU.map(item => <li key={item} className="flex gap-3" data-id="element-50" data-mp="2zb2o5r1lb04f">
                <span aria-hidden="true" className="text-[17px] text-gold" data-id="element-51" data-mp="2uaepcj74dezn">
                  ✓
                </span>
                <span className="text-[17px] leading-[1.6] text-muted" data-id="element-52" data-mp="2pcd7u2pcdnqa">
                  {item}
                </span>
              </li>)}
          </ul>

          <h3 className="mt-12 font-display text-[22px] font-bold leading-[1.3]" data-id="element-53" data-mp="2kn2dh4bkccww">
            No es para ti si…
          </h3>
          <ul className="mt-5 space-y-4" data-id="element-54" data-mp="26nt7exh2mz6n">
            {NOT_FOR_YOU.map(item => <li key={item} className="flex gap-3" data-id="element-55" data-mp="2anlc2z3cgl6q">
                <span aria-hidden="true" className="text-[17px] text-[#8A8A8A]" data-id="element-56" data-mp="2aq6fhsjaj96l">
                  ✕
                </span>
                <span className="text-[17px] leading-[1.6] text-[#8A8A8A]" data-id="element-57" data-mp="33cr4wng7hb7i">
                  {item}
                </span>
              </li>)}
          </ul>

          <button type="button" onClick={scrollToOffer} className="mt-10 h-14 w-full rounded-xl bg-gold text-[15px] font-bold uppercase tracking-wide text-ink shadow-gold transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-goldbright hover:shadow-gold-strong active:translate-y-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-58" data-mp="2pjjako0qq1m4">
            Sí, esto es para mí
          </button>
        </section>

        {/* SECCIÓN 7 — OFERTA */}
        <section ref={offerRef} id="oferta" className="bg-brand-up px-5 py-16" aria-labelledby="oferta-title" data-id="element-59" data-mp="1kd1eiwghynth">
          <div className="rounded-2xl border-2 border-gold bg-ink/70 px-5 py-9 text-center" data-id="element-60" data-mp="rpfcvtqtxt99">
            <img src={COVER_IMG} alt="Todo lo que incluye ELEGIDA en laptop, tablet y celular" className="mx-auto w-full rounded-2xl object-contain" data-id="element-61" data-mp="mqu9jlga6tgl" />
            <h2 id="oferta-title" className="mt-7 font-display text-[28px] font-bold leading-[1.2]" data-id="element-62" data-mp="wo8ofgmk3a6s">
              ELEGIDA
            </h2>
            <p className="mt-2 text-[17px] leading-[1.6] text-muted" data-id="element-63" data-mp="283xckbbjqf96">
              Programa conductual de 30 días
            </p>

            <ul className="mt-7 space-y-4 text-left" data-id="element-64" data-mp="hnlcau375c4k">
              {INCLUDES.map(item => <li key={item} className="flex gap-3" data-id="element-65" data-mp="ml4pdqo5riux">
                  <span aria-hidden="true" className="text-[17px] text-gold" data-id="element-66" data-mp="mnr7gk8odgre">
                    ✓
                  </span>
                  <span className="text-[17px] leading-[1.6] text-muted" data-id="element-67" data-mp="vmcogayi0nq2">
                    {item}
                  </span>
                </li>)}
            </ul>

            <p className="mt-9 font-display text-[48px] font-bold leading-none text-gold" data-id="element-68" data-mp="10juvfvsygsvf">
              $97 USD
            </p>
            <p className="mt-3 text-[13px] leading-[1.6] text-muted" data-id="element-69" data-mp="1aaz2888h97f6">
              Pago único · Garantía de 7 días
            </p>

            <a href={CHECKOUT_URL} className="mt-7 flex h-14 w-full items-center justify-center rounded-xl bg-gold text-[15px] font-bold uppercase tracking-wide text-ink shadow-gold transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-goldbright hover:shadow-gold-strong active:translate-y-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-70" data-mp="1eckkk93s6dwr">
              Quiero mi lugar en ELEGIDA
            </a>
            <p className="mt-4 text-[13px] leading-[1.6] text-muted" data-id="element-71" data-mp="1oj3ovi2rlg36">
              🔒 Pago seguro procesado por Hotmart
            </p>
          </div>

          <div className="mt-8 flex gap-3" data-id="element-72" data-mp="2h4n1a2z3sljq">
            <ShieldCheckIcon className="mt-1 h-5 w-5 shrink-0 text-gold" aria-hidden="true" data-id="element-73" data-mp="2h7sotds33fdc" />
            <p className="text-[15px] leading-[1.6] text-muted" data-id="element-74" data-mp="27fd8sm0cl0v3">
              Garantía de 7 días. Si entras y sientes que no es para ti,
              solicitas tu reembolso a través de Hotmart. Sin preguntas.
            </p>
          </div>
        </section>

        {/* SECCIÓN 8 — PREGUNTAS FRECUENTES */}
        <section className="bg-ink px-6 py-16" aria-labelledby="faq-title" data-id="element-75" data-mp="2v57kh08xkjdr">
          <h2 id="faq-title" className="font-display text-[28px] font-bold leading-[1.2]" data-id="element-76" data-mp="t875cil7ukyj">
            Preguntas frecuentes
          </h2>
          <div className="mt-7 divide-y divide-white/10 border-y border-white/10" data-id="element-77" data-mp="y9364na9tqzq">
            {FAQ_ITEMS.map((item, index) => {
            const isOpen = openFaq === index;
            return <div key={item.q} data-id="element-78" data-mp="oeuhiaod2i6u" data-mpi={index}>
                  <h3 data-id="element-79" data-mp="agwgs82yqk9t">
                    <button type="button" onClick={() => setOpenFaq(isOpen ? null : index)} aria-expanded={isOpen} aria-controls={`faq-panel-${index}`} className="flex w-full items-start justify-between gap-4 py-5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-80" data-mp="agmqeje0ab3l">
                      <span className="text-[17px] font-semibold leading-[1.5] text-white" data-id="element-81" data-mp="1zw3ymqk0oy4z">
                        {item.q}
                      </span>
                      {isOpen ? <MinusIcon className="mt-1 h-5 w-5 shrink-0 text-gold" aria-hidden="true" data-id="element-82" data-mp="3ffbqe1igggh6" /> : <PlusIcon className="mt-1 h-5 w-5 shrink-0 text-gold" aria-hidden="true" data-id="element-83" data-mp="3pdrfy01u3hok" />}
                    </button>
                  </h3>
                  {isOpen && <div id={`faq-panel-${index}`} className="pb-5 pr-9 text-[17px] leading-[1.6] text-muted" data-id="element-84" data-mp="309d08xser0c">
                      {item.a}
                    </div>}
                </div>;
          })}
          </div>
        </section>

        {/* SECCIÓN 9 — CIERRE */}
        <section className="bg-brand px-6 py-16" aria-labelledby="cierre" data-id="element-85" data-mp="2driz6ejtevhf">
          <h2 id="cierre" className="font-display text-[28px] font-bold leading-[1.2]" data-id="element-86" data-mp="1rvr59yutedx4">
            Deja de esperar que esto se resuelva solo.
          </h2>
          <p className="mt-6 text-[17px] leading-[1.6] text-muted" data-id="element-87" data-mp="21mc2bhuubadx">
            Si lo que has hecho hasta ahora funcionara, ya lo tendrías.
          </p>
          <div className="mt-7 border-l-2 border-gold pl-4" data-id="element-88" data-mp="26nrpbfgkvqyy">
            <p className="text-[18px] leading-[1.6] text-white" data-id="element-89" data-mp="18z82rseino9j">
              Prepárate para identificar el patrón de conducta que te está
              haciendo invisible, activar tu{' '}
              <span className="font-semibold text-gold" data-id="element-90" data-mp="1iv2owxhy81h2">
                magnetismo natural
              </span>
              , y aprender la estrategia detrás de toda mujer{' '}
              <span className="font-semibold text-gold" data-id="element-91" data-mp="2ubsrm6n5yb5x">ELEGIDA</span>: la que
              un hombre conquista, elige y conserva.
            </p>
          </div>
          <button type="button" onClick={scrollToOffer} className="mt-8 h-14 w-full rounded-xl bg-gold px-3 text-[15px] font-bold uppercase tracking-wide text-ink shadow-gold transition-[background-color,box-shadow,transform] duration-150 ease-out hover:bg-goldbright hover:shadow-gold-strong active:translate-y-px focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-92" data-mp="2zcgq1ddq1ua6">
            Entrar a ELEGIDA — $97 USD
          </button>
          <p className="mt-4 text-[13px] leading-[1.6] text-muted" data-id="element-93" data-mp="3w329v5nz9vlf">
            Garantía de 7 días · Sesiones grabadas
          </p>
          <p className="mt-12 text-[12px] leading-[1.6] text-[#8A8A8A]" data-id="element-94" data-mp="3r4sil65djf53">
            © 2026 Beatriz Espina · Todos los derechos reservados
          </p>
        </section>
      </main>

      {/* Sticky bar (solo mobile) */}
      <div className={`fixed inset-x-0 bottom-0 z-50 border-t border-gold bg-ink transition-[opacity,transform] duration-200 ease-out md:hidden ${showStickyBar ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none translate-y-full opacity-0'}`} aria-hidden={!showStickyBar} data-id="element-95" data-mp="1pbl2huhxsti5">
        <div className="mx-auto flex w-full max-w-[480px] items-center justify-between gap-4 px-4 py-3" data-id="element-96" data-mp="22grak2rjnxgg">
          <span className="text-[15px] font-semibold text-white" data-id="element-97" data-mp="1xheqtqvyz6i3">
            ELEGIDA · $97 USD
          </span>
          <button type="button" onClick={scrollToOffer} tabIndex={showStickyBar ? 0 : -1} className="h-11 shrink-0 rounded-xl bg-gold px-6 text-[14px] font-bold uppercase tracking-wide text-ink shadow-gold transition-[background-color,box-shadow] duration-150 ease-out hover:bg-goldbright focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-98" data-mp="1thmd6zrpya5v">
            Entrar
          </button>
        </div>
      </div>
    </div>;
}